// seaice.bin: monthly sea-ice concentration on the hexes that ever have ice (layout in
// core/schema.ts, SEA_ICE_MAGIC). The file is optional: a missing or unreadable one leaves
// WorldData.seaIce absent and the map shows no ice, never a guess.

import { HEX_COUNT } from '../core/grid.ts';
import { SEA_ICE_MAGIC } from '../core/schema.ts';
import type { Reader } from './fetch.ts';
import type { SeaIce } from './types.ts';

const FILE = 'seaice.bin';
const VERSION = 1;
const MONTH_AT = 12;
const MONTH_CHARS = 7;
const HEADER_BYTES = 20;
const HEX_ID_BYTES = 4;
const MONTH_TAG = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Decode a seaice.bin buffer; throws an Error whose message says what is wrong with it. */
export function parseSeaIce(buf: ArrayBuffer): SeaIce {
  if (buf.byteLength < HEADER_BYTES) {
    throw new Error(`size ${buf.byteLength} B is shorter than the ${HEADER_BYTES} B header`);
  }

  const view = new DataView(buf);
  const magic = String.fromCharCode(...new Uint8Array(buf, 0, SEA_ICE_MAGIC.length));
  if (magic !== SEA_ICE_MAGIC) {
    throw new Error(`bad magic "${magic}" (expected "${SEA_ICE_MAGIC}")`);
  }

  const version = view.getUint16(4, true);
  if (version !== VERSION) {
    throw new Error(`version ${version} is not supported (expected ${VERSION})`);
  }

  const months = view.getUint16(6, true);
  const count = view.getUint32(8, true);
  const first = String.fromCharCode(...new Uint8Array(buf, MONTH_AT, MONTH_CHARS));
  if (!MONTH_TAG.test(first)) {
    throw new Error(`first month "${first}" is not YYYY-MM`);
  }

  const size = HEADER_BYTES + count * HEX_ID_BYTES + months * count;
  if (months === 0 || buf.byteLength !== size) {
    throw new Error(`size ${buf.byteLength} B does not match ${months} months × ${count} hexes (${size} B)`);
  }

  const hexes = new Uint32Array(buf, HEADER_BYTES, count);
  if (hexes.some((h) => h >= HEX_COUNT)) {
    throw new Error(`a hex id is outside the ${HEX_COUNT}-hex grid`);
  }
  const conc = new Uint8Array(buf, HEADER_BYTES + count * HEX_ID_BYTES, months * count);
  return { first, months, hexes, conc };
}

/** Fetch and decode seaice.bin; null (with a warning) when missing or unreadable. */
export async function loadSeaIce(reader: Reader): Promise<SeaIce | null> {
  const buf = await reader.buffer(FILE);
  if (!buf) {
    return null;
  }

  try {
    return parseSeaIce(buf);
  } catch (err) {
    const msg = `data: ${FILE} is unreadable (${(err as Error).message})`;
    reader.warnings.push(msg);
    console.warn(msg);
    return null;
  }
}
